import React, { useEffect, useMemo, useState } from 'react'
import { PdpResponse, StaticAvailability } from '@/types'
import GalleryProductLayout from '@/components/GalleryProductLayout'
import MinimalProductLayout from '@/components/MinimalProductPage'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import Head from 'next/head'
import 'react-datepicker/dist/react-datepicker.css'
import { useAuthSession } from '@/hooks/AuthSessionHook'
import { BookingContext } from '@/context/BookingContext'
import { useCustomerCertificates } from '@/hooks/CustomerCertificateHook'
import {
  getCartExperienceVoucherIds,
  getProductExperienceVouchers,
  useCart,
} from '@big-red-group/storefront-common-checkout'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { JsendResponse } from '@big-red-group/storefront-common-checkout/dist/types'

interface ProductDetailsPageProps {
  catalogueResponse: PdpResponse
  canonicalLink: string
}

const ProductDetailsPage: React.FC<ProductDetailsPageProps> = ({ catalogueResponse, canonicalLink }) => {
  const {
    productDetails,
    productRecommendations,
    productReviews,
    moreToExplore,
    agentConfiguration,
    loyaltyCampaign,
    lastMinutePricingDates,
  } = catalogueResponse
  const defaultProperties = useDefaultProperties()
  const productLayout = defaultProperties?.agentConfiguration?.productLayout || 'productGalleryHero'
  const agentHandlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const baseUrl = getUrlPrefix(agentHandlerKey)
  const { user } = useAuthSession()
  const { certificates, loadCertificates } = useCustomerCertificates()
  const { data: cart } = useCart('cart', baseUrl)

  const [staticAvailability, setStaticAvailability] = useState<StaticAvailability | {}>({})

  const fetchStaticAvailability = async (productId: number): Promise<JsendResponse<StaticAvailability>> => {
    const res = await fetch('/api/availability/static', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ productId }),
    })
    const response = await res.json()
    return response.data
  }

  useEffect(() => {
    if (!productDetails.bookingRequired) {
      fetchStaticAvailability(productDetails.id).then(setStaticAvailability)
    }
  }, [productDetails.id, productDetails.bookingRequired])

  const appliedEvs = useMemo(() => {
    return cart ? getCartExperienceVoucherIds(cart) : []
  }, [cart?.items])

  const productExperienceVouchers = useMemo(
    () => getProductExperienceVouchers(certificates, productDetails.id, appliedEvs),
    [certificates, appliedEvs, productDetails.id]
  )

  const hasProductCertificates = useMemo(
    () => Boolean(user?.evProductIds?.includes(productDetails.id)),
    [productDetails.id, user]
  )

  // Load user certificates if a EV for the current product is detected
  useEffect(() => {
    if (hasProductCertificates) {
      loadCertificates()
    }
  }, [hasProductCertificates])

  const commonProps = {
    productDetails,
    productRecommendations,
    productReviews,
    moreToExplore,
    agentConfiguration,
    loyaltyCampaign,
    canonicalLink,
    lastMinutePricingDates,
    images: productDetails.images,
    staticAvailability: staticAvailability,
  }

  const bookingContextValue = useMemo(
  () => ({
      useRedemptionLayout: productExperienceVouchers.length > 0,
      certificates: productExperienceVouchers,
      appliedEvs,
    }),
    [productExperienceVouchers, appliedEvs]
  )

  return (
    <>
      <Head>
        <meta name="pageid" content="pdp" />
      </Head>
      <BookingContext.Provider value={bookingContextValue}>
        {productLayout === 'productGalleryHero' ? (
          <GalleryProductLayout {...commonProps} />
        ) : (
          <MinimalProductLayout {...commonProps} />
        )}
      </BookingContext.Provider>
    </>
  )
}

export default ProductDetailsPage
